/**
 * /cli/connect?code=ABCD-EFGH — the browser half of `trodo login`
 * (backend services/cli/deviceAuth). The terminal shows the same code; the
 * person checks it, picks the team the CLI works on, and connects. A Trodo
 * API key is made for them on that team and handed to the terminal once.
 * Chrome copied from ConnectMCP.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Terminal } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';

export default function ConnectCLI() {
  const [params] = useSearchParams();
  const code = (params.get('code') || '').toUpperCase();
  const [request, setRequest] = useState(null);      // null = loading
  const [teams, setTeams] = useState(null);
  const [teamId, setTeamId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);            // { status, team? }

  useEffect(() => {
    let alive = true;
    axiosInstance.get(`/cli-auth/${encodeURIComponent(code)}`)
      .then(({ data }) => { if (alive) setRequest(data); })
      .catch((e) => { if (alive) { setRequest(false); setError(e?.response?.data?.message || 'This sign-in code expired or was used. Run trodo login again.'); } });
    axiosInstance.get('/team/details')
      .then(({ data }) => {
        if (!alive) return;
        const list = (Array.isArray(data?.team) ? data.team : []).filter((t) => !t.is_demo);
        setTeams(list);
        const stored = localStorage.getItem('selectedTeam');
        const pick = list.find((t) => t.name === stored) || list[0];
        if (pick) setTeamId(String(pick.id));
      })
      .catch(() => { if (alive) setTeams([]); });
    return () => { alive = false; };
  }, [code]);

  const team = useMemo(() => (teams || []).find((t) => String(t.id) === teamId) || null, [teams, teamId]);

  const answer = async (what) => {
    setBusy(true); setError('');
    try {
      const { data } = what === 'approve'
        ? await axiosInstance.post(`/cli-auth/${encodeURIComponent(code)}/approve`, { team_id: Number(teamId) })
        : await axiosInstance.post(`/cli-auth/${encodeURIComponent(code)}/deny`);
      setDone(data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--trodo-primary)' }}>
        <Terminal size={12} aria-hidden /> Trodo · CLI
      </div>
      {done ? (
        <>
          <h1 className="mt-2 text-2xl font-semibold leading-tight text-[color:var(--text-primary)]">
            {done.status === 'approved' ? 'Terminal connected' : 'Sign-in refused'}
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-[color:var(--text-muted)]">
            {done.status === 'approved'
              ? `The CLI now works on ${done.team?.name || 'your team'}. You can close this tab.`
              : 'Nothing was connected. You can close this tab.'}
          </p>
        </>
      ) : (
        <>
          <h1 className="mt-2 text-2xl font-semibold leading-tight text-[color:var(--text-primary)]">Connect the Trodo CLI</h1>
          <p className="mt-2 text-[14px] leading-relaxed text-[color:var(--text-muted)]">
            Check that your terminal shows this code, then choose the team it works on.
          </p>
          <div className="mt-5 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-4 py-3 text-center font-mono text-[22px] font-semibold tracking-[0.2em] text-[color:var(--text-primary)]" aria-label="Sign-in code">
            {code || '—'}
          </div>
          {request ? (
            <p className="mt-2 text-center text-[12px] text-[color:var(--text-muted)]">
              {[request.client, request.host].filter(Boolean).join(' on ') || 'A terminal'}
            </p>
          ) : null}

          <div className="mb-2 mt-6 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]">Team</div>
          {teams === null ? (
            <div className="h-10 w-full animate-pulse rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-hover)]" aria-hidden />
          ) : teams.length === 0 ? (
            <p className="text-[13px] text-[color:var(--text-muted)]">You are not in a team yet.</p>
          ) : (
            <select aria-label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}
              className="w-full cursor-pointer rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] py-2.5 pl-3 pr-3 text-[14px] text-[color:var(--text-primary)] focus:border-[color:var(--trodo-primary)] focus:outline-none">
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name || `Team ${t.id}`}</option>)}
            </select>
          )}
          <p className="mt-2 text-[12px] leading-relaxed text-[color:var(--text-muted)]">
            The CLI reads everything on this team, as you, and only adds: datasets, rows, prompt versions, feedback, agent runs. It cannot delete anything. Revoke it any time in Settings → MCP.
          </p>

          {error ? <p role="alert" className="mt-4 text-[13px] text-danger">{error}</p> : null}

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => answer('deny')} disabled={busy || !request}
              className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-4 py-2 text-[13px] font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--surface-hover)] disabled:opacity-50">
              Cancel
            </button>
            <button type="button" onClick={() => answer('approve')} disabled={busy || !request || !team}
              className="rounded-md px-4 py-2 text-[13px] font-medium text-ink-onBrand hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: 'var(--trodo-primary)' }}>
              {busy ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="max-h-[100dvh] min-h-0 w-full overflow-y-auto bg-[color:var(--surface-base)] py-6 sm:py-10" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="mx-auto w-full max-w-[480px] px-4 pb-10">
        <div className="rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );
}
